import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import PipelineStatus from '../components/PipelineStatus';

export default function Marketplace() {
    const [activeAssets, setActiveAssets] = useState([]);
    const [plants, setPlants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [theme, setTheme] = useState(localStorage.getItem('theme') || 'dark');

    // Purchase Modal State
    const [purchaseModal, setPurchaseModal] = useState(null);
    const [purchaseQty, setPurchaseQty] = useState(1);
    const [purchaseStep, setPurchaseStep] = useState(1);

    // Filtering & Expansion
    const [plantFilter, setPlantFilter] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedAssetId, setExpandedAssetId] = useState(null);

    useEffect(() => {
        document.title = "Clean Energy Marketplace | Silicon Himalayas";
        fetchData();
        const interval = setInterval(fetchData, 10000);
        return () => clearInterval(interval);
    }, []);

    const fetchData = async () => {
        try {
            const [resActive, resPlants] = await Promise.all([
                api.get('/api/investor/attestations?status=active'),
                api.get('/api/plants')
            ]);
            setActiveAssets(resActive.data || []);
            setPlants(resPlants.data || []);
            setLoading(false);
        } catch (err) {
            console.error("Fetch failed", err);
            setLoading(false);
        }
    };

    const handlePurchase = async () => {
        if (!purchaseModal) return;
        setSubmitting(true);
        setError('');
        try {
            await api.post(`/api/investor/purchase/${purchaseModal.id}`, {
                quantity: purchaseQty,
                purpose: 'Scope 2 ESG Reporting',
                sandboxNote: 'Simulated purchase — NRB sandbox phase'
            });
            setPurchaseStep(3);
            setTimeout(() => {
                setPurchaseModal(null);
                setPurchaseStep(1);
                setPurchaseQty(1);
                fetchData();
                setMessage('✓ Certificate purchased successfully. Ready for retirement in your portfolio.');
                setTimeout(() => setMessage(''), 6000);
            }, 2000);
        } catch (err) {
            setError(err.response?.data?.error || 'Purchase failed. Please try again.');
            setPurchaseStep(1);
        } finally {
            setSubmitting(false);
        }
    };

    const toggleTheme = () => {
        const newTheme = theme === 'dark' ? 'light' : 'dark';
        setTheme(newTheme);
        localStorage.setItem('theme', newTheme);
    };

    if (loading) {
        return (
            <div className="h-screen flex items-center justify-center bg-[#080a0f] text-emerald-500 font-black tracking-widest animate-pulse">
                SYNCHRONIZING MARKETPLACE...
            </div>
        );
    }

    const filteredAssets = activeAssets.filter(a => {
        const matchesPlant = plantFilter ? (String(a.plantId || a.projectId) === String(plantFilter)) : true;
        const matchesSearch = searchQuery
            ? (a.id.toLowerCase().includes(searchQuery.toLowerCase()) || 
               (a.plantName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
               (a.projectId || '').toLowerCase().includes(searchQuery.toLowerCase()))
            : true;
        return matchesPlant && matchesSearch;
    });

    return (
        <div className={`min-h-screen transition-colors duration-500 ${theme === 'dark' ? 'bg-[#0a0c12] text-slate-200' : 'bg-slate-50 text-slate-600'} p-4 md:p-8 font-sans`}>
            {/* Header */}
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 max-w-7xl mx-auto gap-6 flex-wrap">
                <Link to="/dashboard/investor">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                            <svg className="w-8 h-8 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M2 20L8 8L13 16" />
                                <path d="M8 20L15 6L22 20" />
                                <path d="M8 8L15 6" strokeDasharray="1.5 1.5" />
                                <circle cx="15" cy="6" r="1.5" fill="currentColor" />
                            </svg>
                        </div>
                        <h1 className={`text-3xl font-bold tracking-tight ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
                            Silicon Himalayas <span className="text-emerald-500 italic font-black">Marketplace</span>
                        </h1>
                    </div>
                    <p className="text-slate-500 text-xs mt-1 uppercase tracking-[0.3em] font-black ml-13">Verified Renewable Certificates</p>
                </Link>
                <div className="flex gap-4 items-center flex-wrap w-full md:w-auto">
                    <Link 
                        to="/dashboard/investor" 
                        className={`px-5 py-2.5 rounded-2xl border font-bold text-xs uppercase tracking-wider transition-all flex items-center gap-2 ${
                            theme === 'dark' 
                                ? 'bg-white/5 border-white/10 text-slate-350 hover:bg-white/10' 
                                : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 shadow-sm'
                        }`}
                    >
                        💼 Back to Portfolio
                    </Link>
                    <button 
                        onClick={toggleTheme} 
                        className={`p-3 rounded-2xl border transition-all ${
                            theme === 'dark' 
                                ? 'bg-white/5 border-white/10 text-yellow-500 hover:bg-white/10' 
                                : 'bg-white border-slate-200 text-slate-800 hover:bg-slate-50 shadow-sm'
                        }`}
                    >
                        {theme === 'dark' ? '☀️' : '🌙'}
                    </button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto space-y-10">
                {(message || error) && (
                    <div className={`${error ? 'bg-rose-500/10 text-rose-400 border-rose-500/20' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'} p-4 rounded-3xl border flex items-center gap-3 animate-slide-in`}>
                        <div className={`w-2 h-2 rounded-full animate-pulse ${error ? 'bg-rose-500' : 'bg-emerald-500'}`}></div>
                        <span className="font-bold text-sm tracking-wide uppercase">{message || error}</span>
                    </div>
                )}

                <section className={`${theme === 'dark' ? 'glass border-white/5 shadow-2xl' : 'bg-white border-slate-200 shadow-xl'} rounded-[2.5rem] border overflow-hidden animate-fade-in`}>
                    <div className="p-8 border-b flex justify-between items-center flex-wrap gap-4 bg-slate-900/10">
                        <div className="flex items-center gap-4">
                            <div>
                                <h2 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>Active Certificate Listings</h2>
                                <p className="text-[10px] text-slate-500 mt-1 font-medium uppercase tracking-wider">
                                    Verified Clean Energy Outputs Available for Purchase & Offset Retirement
                                </p>
                            </div>
                            <span className="bg-emerald-500/20 text-emerald-400 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span>
                                Live Market
                            </span>
                        </div>
                        <div className="flex items-center gap-4 flex-wrap">
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] text-slate-500 uppercase tracking-widest">Search:</span>
                                <input 
                                    type="text" 
                                    placeholder="Certificate or Plant ID..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    className={`bg-transparent border ${
                                        theme === 'dark' ? 'border-white/10 text-slate-350 focus:border-emerald-500/50' : 'border-slate-300 text-slate-700 focus:border-emerald-500'
                                    } rounded-xl py-1.5 px-4 text-xs focus:outline-none transition-all w-48`}
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] text-slate-500 uppercase tracking-widest">Plant:</span>
                                <select 
                                    value={plantFilter}
                                    onChange={e => setPlantFilter(e.target.value)}
                                    className={`bg-transparent border ${
                                        theme === 'dark' ? 'border-white/10 text-slate-300 focus:border-emerald-500' : 'border-slate-300 text-slate-700 focus:border-emerald-500'
                                    } rounded-xl py-2 px-4 text-xs focus:outline-none transition-all`}
                                >
                                    <option value="" className={theme === 'dark' ? 'bg-[#0a0c12] text-slate-300' : 'bg-white text-slate-700'}>All Source Plants</option>
                                    {plants.map(p => (
                                        <option key={p.id} value={p.id} className={theme === 'dark' ? 'bg-[#0a0c12] text-slate-300' : 'bg-white text-slate-700'}>
                                            {p.plant_name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                    
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className={`text-[10px] font-black uppercase tracking-widest border-b ${theme === 'dark' ? 'text-slate-500 border-white/5' : 'text-slate-400 border-slate-100'}`}>
                                <tr>
                                    <th className="p-5 pl-8">Certificate ID</th>
                                    <th className="p-5">Vintage</th>
                                    <th className="p-5">Volume</th>
                                    <th className="p-5">Source Project</th>
                                    <th className="p-5">Technology</th>
                                    <th className="p-5">I-REC Status</th>
                                    <th className="p-5">Price</th>
                                    <th className="p-5 text-right pr-8">Action</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${theme === 'dark' ? 'divide-white/5' : 'divide-slate-100'}`}>
                                {filteredAssets.map(a => {
                                    const matchingPlant = plants.find(p => String(p.id) === String(a.plantId || a.projectId));
                                    return (
                                        <React.Fragment key={a.id}>
                                            <tr 
                                                className={`group hover:bg-emerald-500/5 transition-colors cursor-pointer ${
                                                    expandedAssetId === a.id ? (theme === 'dark' ? 'bg-white/2' : 'bg-slate-50/50') : ''
                                                }`}
                                                onClick={() => setExpandedAssetId(expandedAssetId === a.id ? null : a.id)}
                                            >
                                                <td className="p-5 pl-8">
                                                    <div className="flex items-center gap-3">
                                                        <span className="text-[10px] text-slate-500 transition-transform duration-200">
                                                            {expandedAssetId === a.id ? '▼' : '▶'}
                                                        </span>
                                                        <div>
                                                            <p className="font-mono text-[10px] opacity-75 mb-0.5">{a.id.substring(0, 14)}...</p>
                                                            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">SHL-{new Date(a.timestamp).getFullYear()}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="p-5">
                                                    <span className="text-xs font-bold">
                                                        {a.vintagePeriod || new Date(a.timestamp).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                                                    </span>
                                                </td>
                                                <td className="p-5">
                                                    <p className="font-bold text-lg tracking-tight text-emerald-400">
                                                        {a.mwh} <span className="text-xs text-slate-500 ml-0.5 font-normal">MWh</span>
                                                    </p>
                                                    <p className="text-[9px] text-slate-500 font-bold uppercase tracking-wider mt-0.5">
                                                        = {a.mwh} I-REC(E)
                                                    </p>
                                                </td>
                                                <td className="p-5">
                                                    {(a.plantId || a.projectId) ? (
                                                        <Link 
                                                            to={`/plant/${a.plantId || a.projectId}`} 
                                                            className="text-cyan-400 hover:text-cyan-300 text-xs font-bold hover:underline block" 
                                                            onClick={e => e.stopPropagation()}
                                                        >
                                                            {a.plantName || (matchingPlant?.plant_name) || a.projectId}
                                                        </Link>
                                                    ) : (
                                                        <span className="text-slate-500 text-xs">—</span>
                                                    )}
                                                    <p className="text-[9px] text-slate-600 mt-0.5">Nepal · Grid-connected</p>
                                                </td>
                                                <td className="p-5">
                                                    <span className={`text-[9px] font-black uppercase px-2 py-1 rounded-lg block w-fit ${
                                                        (a.fuelType || 'Solar').toLowerCase() === 'solar' 
                                                            ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/15' 
                                                            : 'bg-blue-500/10 text-blue-400 border border-blue-500/15'
                                                    }`}>
                                                        {a.fuelType || 'Solar'} · {a.technologyType || 'PV'}
                                                    </span>
                                                </td>
                                                <td className="p-5">
                                                    {a.evidentSerial ? (
                                                        <div>
                                                            <span className="text-[9px] font-bold text-emerald-400 block">✓ Evident Issued</span>
                                                            <span className="font-mono text-[8px] text-slate-500">{a.evidentSerial}</span>
                                                        </div>
                                                    ) : (
                                                        <span className="text-[9px] text-yellow-500 font-bold uppercase tracking-wide">⏳ Pending GCC</span>
                                                    )}
                                                </td>
                                                <td className="p-5">
                                                    <p className={`font-bold ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>${a.price || '0.85'}</p>
                                                    <p className="text-[9px] text-slate-500 font-bold">per MWh</p>
                                                    <p className="text-[9px] text-slate-500 italic mt-0.5">≈ ${(parseFloat(a.price || 0.85) * parseFloat(a.mwh || 1)).toFixed(2)} total</p>
                                                </td>
                                                <td className="p-5 text-right pr-8" onClick={e => e.stopPropagation()}>
                                                    <button 
                                                        onClick={() => {
                                                            setPurchaseQty(1);
                                                            setPurchaseStep(1);
                                                            setPurchaseModal(a);
                                                        }} 
                                                        className="px-6 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold rounded-xl text-[10px] uppercase tracking-widest transition transform active:scale-95 shadow-lg shadow-emerald-500/10"
                                                    >
                                                        Purchase
                                                    </button>
                                                </td>
                                            </tr>
                                            
                                            {expandedAssetId === a.id && (
                                                <tr className={theme === 'dark' ? 'bg-white/2' : 'bg-slate-50/20'}>
                                                    <td colSpan="8" className="p-6 pl-8 pr-8 border-b border-white/5">
                                                        <div className="space-y-6">
                                                            {/* Pipeline Status */}
                                                            <div>
                                                                <div className="flex justify-between items-center px-2 mb-2">
                                                                    <h4 className="text-xs font-black uppercase tracking-widest text-slate-400">Onboarding & Issuance Pipeline</h4>
                                                                    {a.evidentSerial && (
                                                                        <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded border border-emerald-500/20">Evident Serial: {a.evidentSerial}</span>
                                                                    )}
                                                                </div>
                                                                <PipelineStatus 
                                                                    attestation={a} 
                                                                    plant={matchingPlant} 
                                                                />
                                                            </div>

                                                            <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 p-5 rounded-2xl border text-xs ${
                                                                theme === 'dark' ? 'border-white/5 bg-[#0a0c12]/50 text-slate-350' : 'border-slate-200 bg-slate-50 text-slate-650'
                                                            }`}>
                                                                <div>
                                                                    <span className="block text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-1">Fuel Type</span>
                                                                    <span className={`font-semibold ${theme === 'dark' ? 'text-slate-200' : 'text-slate-800'}`}>{a.fuelType || 'Solar'}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="block text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-1">Technology</span>
                                                                    <span className={`font-semibold ${theme === 'dark' ? 'text-slate-200' : 'text-slate-800'}`}>{a.technologyType || 'Photovoltaic'}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="block text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-1">Vintage Period</span>
                                                                    <span className={`font-semibold ${theme === 'dark' ? 'text-slate-200' : 'text-slate-800'}`}>{a.vintagePeriod || new Date(a.timestamp).toLocaleDateString('en-US', {month:'short', year:'numeric'})}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="block text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-1">Volume (MWh)</span>
                                                                    <span className="font-semibold text-emerald-500">{a.mwh} MWh</span>
                                                                </div>
                                                                <div className="col-span-1 sm:col-span-2">
                                                                    <span className="block text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-1">Ledger Transaction ID</span>
                                                                    <span className="font-mono text-[11px] text-slate-400 block break-all select-all">{a.id}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="block text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-1">Registry Standard</span>
                                                                    <span className={`font-semibold ${theme === 'dark' ? 'text-slate-200' : 'text-slate-800'}`}>I-REC Compliant (GCC/Evident)</span>
                                                                </div>
                                                                <div>
                                                                    <span className="block text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-1">Smart Contract Hash</span>
                                                                    <span className="font-mono text-[10px] text-slate-400 block truncate" title={a.sensorDid || a.issuer}>Signature: {a.sensorSignature?.substring(0, 18) || 'N/A'}...</span>
                                                                </div>
                                                            </div>

                                                            {matchingPlant && (
                                                                <div className={`p-5 rounded-2xl border text-xs space-y-3 ${
                                                                    theme === 'dark' ? 'border-white/5 bg-[#0a0c12]/30' : 'border-slate-200 bg-slate-50/50'
                                                                }`}>
                                                                    <h5 className="text-[10px] font-black uppercase tracking-widest text-slate-400">Project Details</h5>
                                                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                                                                        <div>
                                                                            <span className="block text-[9px] uppercase tracking-widest text-slate-500 mb-0.5">Location</span>
                                                                            <span className={`font-semibold ${theme === 'dark' ? 'text-slate-350' : 'text-slate-700'}`}>{matchingPlant.location}</span>
                                                                        </div>
                                                                        <div>
                                                                            <span className="block text-[9px] uppercase tracking-widest text-slate-500 mb-0.5">Capacity</span>
                                                                            <span className={`font-semibold ${theme === 'dark' ? 'text-slate-350' : 'text-slate-700'}`}>{matchingPlant.capacity_mw} MW</span>
                                                                        </div>
                                                                        <div>
                                                                            <span className="block text-[9px] uppercase tracking-widest text-slate-500 mb-0.5">Owner</span>
                                                                            <span className={`font-semibold ${theme === 'dark' ? 'text-slate-350' : 'text-slate-700'}`}>{matchingPlant.owner_name}</span>
                                                                        </div>
                                                                        <div>
                                                                            <span className="block text-[9px] uppercase tracking-widest text-slate-500 mb-0.5">Commissioned</span>
                                                                            <span className={`font-semibold ${theme === 'dark' ? 'text-slate-350' : 'text-slate-700'}`}>{new Date(matchingPlant.commissioning_date).getFullYear()}</span>
                                                                        </div>
                                                                    </div>
                                                                    <div className={`pt-2 border-t flex flex-wrap gap-x-6 gap-y-2 text-[10px] uppercase tracking-wider font-semibold ${
                                                                        theme === 'dark' ? 'border-white/5 text-slate-500' : 'border-slate-200 text-slate-500'
                                                                    }`}>
                                                                        <span>Grid Connection: <span className="text-emerald-500">✓ Approved</span></span>
                                                                        <span>COD Documentation: <span className="text-emerald-500">✓ Approved</span></span>
                                                                        <span>Metering Verification: <span className="text-emerald-550">✓ Verified</span></span>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                        {filteredAssets.length === 0 && (
                            <div className="p-24 text-center text-slate-500 font-bold uppercase tracking-widest text-xs italic">
                                No certificates currently listed matching search filters.
                            </div>
                        )}
                    </div>
                </section>
            </main>

            {/* Purchase Modal */}
            {purchaseModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 animate-fade-in shadow-2xl">
                    <div className="absolute inset-0 bg-black/90 backdrop-blur-xl" onClick={() => !submitting && setPurchaseModal(null)}></div>
                    <div className={`${theme === 'dark' ? 'glass border-white/10' : 'bg-white border-slate-200'} w-full max-w-lg rounded-[2rem] sm:rounded-[2.5rem] border relative z-10 shadow-2xl animate-scale-in`}>
                        {/* Modal Header */}
                        <div className={`p-5 sm:p-8 border-b ${theme === 'dark' ? 'border-white/5' : 'border-slate-100'} flex justify-between items-center`}>
                            <div>
                                <h3 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>
                                    {purchaseStep === 3 ? '✓ Purchase Complete' : 'Purchase I-REC Certificate'}
                                </h3>
                                <p className="text-[10px] text-slate-500 mt-1 uppercase tracking-widest font-bold">
                                    {purchaseStep === 1 ? 'Step 1: Review' : purchaseStep === 2 ? 'Step 2: Confirm' : 'Added to your portfolio'}
                                </p>
                            </div>
                            {purchaseStep !== 3 && (
                                <button onClick={() => setPurchaseModal(null)} className="p-2 hover:bg-white/10 rounded-full transition text-slate-500"><svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg></button>
                            )}
                        </div>

                        <div className="p-5 sm:p-8 space-y-6">
                            {/* Step 3 success state */}
                            {purchaseStep === 3 && (
                                <div className="text-center py-8">
                                    <div className="text-6xl mb-4">🌿</div>
                                    <p className="text-emerald-400 font-bold text-lg mb-2">Certificate Reserved</p>
                                    <p className="text-slate-500 text-sm">
                                        Retire this certificate in your portfolio to complete your Scope 2 ESG claim.
                                    </p>
                                </div>
                            )}

                            {/* Steps 1 and 2 */}
                            {purchaseStep < 3 && (
                                <>
                                    {/* Certificate summary */}
                                    <div className="bg-emerald-500/5 p-5 rounded-2xl border border-emerald-500/20 space-y-3">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <p className="text-[9px] text-emerald-500 font-black uppercase tracking-widest mb-1">I-REC Certificate</p>
                                                <p className={`font-bold text-xl ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>{purchaseModal.mwh} MWh</p>
                                                <p className="text-[10px] text-slate-500 mt-1">
                                                    {purchaseModal.plantName || purchaseModal.plant_name || 'Nepal Solar'} · {purchaseModal.vintagePeriod || new Date(purchaseModal.timestamp).toLocaleDateString('en-US', {month:'short', year:'numeric'})}
                                                </p>
                                            </div>
                                            <span className="text-[9px] font-bold px-3 py-1 rounded-full bg-yellow-500/10 text-yellow-400 border border-yellow-500/15">
                                                {purchaseModal.fuelType || 'Solar'} PV
                                            </span>
                                        </div>
                                        <div className="pt-3 border-t border-emerald-500/10 flex justify-between">
                                            <span className="text-[10px] text-slate-500">Price per MWh</span>
                                            <span className={`text-sm font-bold ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>USD ${purchaseModal.price || '0.85'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-[10px] text-slate-500">Total</span>
                                            <span className="text-sm font-bold text-emerald-400">USD ${(parseFloat(purchaseModal.price || 0.85) * parseFloat(purchaseModal.mwh || 1)).toFixed(2)}</span>
                                        </div>
                                    </div>

                                    {/* Scope 2 info box */}
                                    <div className="bg-blue-500/5 p-4 rounded-2xl border border-blue-500/10">
                                        <p className="text-[9px] text-blue-400 font-black uppercase tracking-widest mb-2">ESG Reporting Use</p>
                                        <p className="text-xs text-slate-400 leading-relaxed">
                                            This certificate can be retired for Scope 2 market-based reporting under GHG Protocol. Upon retirement, a PDF retirement certificate is generated for your sustainability report.
                                        </p>
                                    </div>

                                    {/* Step 1 sandbox notice */}
                                    {purchaseStep === 1 && (
                                        <div className="bg-yellow-500/5 p-4 rounded-2xl border border-yellow-500/10">
                                            <p className="text-[9px] text-yellow-500 font-black uppercase tracking-widest mb-1">⚠ Sandbox Mode</p>
                                            <p className="text-[10px] text-slate-500">
                                                This is a simulated purchase under the NRB regulatory sandbox. No real payment is processed. Live payments activate post-license.
                                            </p>
                                        </div>
                                    )}

                                    {/* I-REC status warning if no Evident serial */}
                                    {!purchaseModal.evidentSerial && purchaseStep === 1 && (
                                        <div className="bg-slate-500/5 p-4 rounded-2xl border border-slate-500/10">
                                            <p className="text-[9px] text-slate-400 font-black uppercase tracking-widest mb-1">GCC/Evident Registration Pending</p>
                                            <p className="text-[10px] text-slate-500">
                                                Formal I-REC issuance via Evident Registry is in progress. Certificate will carry a valid Evident serial upon project commissioning.
                                            </p>
                                        </div>
                                    )}

                                    {/* Action buttons */}
                                    <div className="flex gap-3">
                                        {purchaseStep === 1 && (
                                            <>
                                                <button onClick={() => setPurchaseModal(null)} className={`flex-1 py-4 border rounded-2xl font-bold text-[10px] uppercase tracking-widest transition ${
                                                    theme === 'dark' ? 'glass border-white/10 text-slate-400 hover:bg-white/5' : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
                                                }`}>
                                                    Cancel
                                                </button>
                                                <button onClick={() => setPurchaseStep(2)} className="flex-1 py-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-emerald-500/20 transition">
                                                    Review & Confirm →
                                                </button>
                                            </>
                                        )}
                                        {purchaseStep === 2 && (
                                            <>
                                                <button onClick={() => setPurchaseStep(1)} className={`flex-1 py-4 border rounded-2xl font-bold text-[10px] uppercase tracking-widest transition ${
                                                    theme === 'dark' ? 'glass border-white/10 text-slate-400 hover:bg-white/5' : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
                                                }`}>
                                                    ← Back
                                                </button>
                                                <button onClick={handlePurchase} disabled={submitting} className="flex-1 py-4 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-emerald-500/20 transition active:scale-95">
                                                    {submitting ? 'Processing...' : 'Confirm Purchase'}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
